import { getPath, type RegistryEntry } from "@tcr/contracts";

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** "12s ago", "4m ago", "3h ago", "2d ago". */
export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Robot clocks before this are not wall-clock time: a simulator publishing ROS sim time
 * (seconds since the simulation started) or a robot whose clock was never set reads near 1970.
 */
export const MIN_PLAUSIBLE_ROBOT_TIME = Date.UTC(2020, 0, 1);
/** A robot timestamp this far ahead of the platform receiving it is a skewed clock. */
export const MAX_ROBOT_CLOCK_AHEAD_MS = 5 * 60_000;

export interface EventTime {
  /** The time to show. */
  iso: string | null;
  /** Whose clock `iso` comes from. */
  source: "robot" | "platform";
  /** The robot sent a timestamp that cannot be wall-clock time; `iso` falls back to the platform's receipt time. */
  robotClockInvalid: boolean;
}

/**
 * Pick an honest time for a robot-reported event. The robot's own timestamp is preferred,
 * including legitimately old ones (an error raised before the robot connected); an implausible
 * one falls back to when the platform first received it.
 */
export function eventTime(robotIso: string | null | undefined, platformIso: string | null | undefined): EventTime {
  const robot = robotIso ? Date.parse(robotIso) : NaN;
  const platform = platformIso ? Date.parse(platformIso) : NaN;
  const invalid = !Number.isFinite(robot) || robot < MIN_PLAUSIBLE_ROBOT_TIME || (Number.isFinite(platform) && robot - platform > MAX_ROBOT_CLOCK_AHEAD_MS);
  if (!invalid) return { iso: robotIso!, source: "robot", robotClockInvalid: false };
  return { iso: platformIso ?? null, source: "platform", robotClockInvalid: !!robotIso };
}

export function formatAge(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds)) return "—";
  const neg = totalSeconds < 0;
  let s = Math.round(Math.abs(totalSeconds));
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  const parts = h ? [`${h}h`, `${m}m`, `${s}s`] : m ? [`${m}m`, `${s}s`] : [`${s}s`];
  return (neg ? "-" : "") + parts.join(" ");
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function formatPercent(fraction: number | null | undefined, digits = 0): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return "—";
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** Progress may arrive as a 0..1 fraction or a 0..100 percentage; normalise to 0..1. */
export function progressFraction(p: number | null | undefined): number | null {
  if (p === null || p === undefined || !Number.isFinite(p)) return null;
  return Math.max(0, Math.min(1, p > 1 ? p / 100 : p));
}

export function formatProgress(p: number | null | undefined): string {
  const f = progressFraction(p);
  return f === null ? "—" : `${Math.round(f * 100)}%`;
}

export function humanize(s: string): string {
  return s.replace(/[_.]/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : "—";
}

export const RAD2DEG = 180 / Math.PI;
export const DEG2RAD = Math.PI / 180;

type MetricDef = NonNullable<RegistryEntry["metric"]>;

/**
 * Format a telemetry value for display using a registry metric definition.
 * `percent` expects a 0..1 fraction (ROS convention, e.g. BatteryState.percentage);
 * values > 1 are assumed to already be percentages. `duration` expects seconds.
 */
export function formatMetricValue(raw: unknown, metric: Pick<MetricDef, "format" | "precision" | "unit">): string {
  if (raw === undefined || raw === null) return "—";
  switch (metric.format) {
    case "boolean": {
      const b = typeof raw === "string" ? raw === "true" || raw === "1" : Boolean(raw);
      return b ? "Yes" : "No";
    }
    case "text":
      return typeof raw === "object" ? JSON.stringify(raw) : String(raw);
    case "percent": {
      const n = Number(raw);
      if (!Number.isFinite(n)) return String(raw);
      const pct = Math.abs(n) <= 1 ? n * 100 : n;
      return `${pct.toFixed(metric.precision)}%`;
    }
    case "duration": {
      const n = Number(raw);
      return Number.isFinite(n) ? formatDuration(n) : String(raw);
    }
    case "number":
    default: {
      const n = Number(raw);
      if (!Number.isFinite(n)) return typeof raw === "object" ? JSON.stringify(raw) : String(raw);
      const v = n.toFixed(metric.precision);
      return metric.unit ? `${v} ${metric.unit}` : v;
    }
  }
}

/** Extract a metric value from a telemetry sample's data (falls back to `value`). */
export function metricValue(data: unknown, value: unknown, valuePath: string): unknown {
  const fromData = getPath(data, valuePath);
  if (fromData !== undefined) return fromData;
  const fromValue = getPath(value, valuePath);
  return fromValue !== undefined ? fromValue : value;
}

export function newUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // RFC4122 v4 fallback for non-secure contexts (e.g. http on a LAN tablet).
  const b = new Uint8Array(16);
  for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
